import {
  ProductItem,
  MarkupAmountType,
} from 'lane-shared/domains/billingPayments/types';
import BigNumber from 'bignumber.js';
import {
  getCurrencyPrecision,
  type SupportedPaymentCurrency,
} from 'domains-billing-currency';

/**
 * Calculate the minimum allowed rate for a product item
 * based on fixed markup and tax amounts
 */
export const getMinimumRate = (item: ProductItem): number => {
  if (!item) return 0;

  let minRate = 0;

  // Add fixed markup if present
  if (
    item.product.markup?.markupAmountType ===
    MarkupAmountType.MarkupAmountTypeFixed
  ) {
    minRate += item.product.markup.value || 0;
  }

  return minRate;
};

/**
 * Calculate the new base amount from a display rate
 * considering markup type (fixed vs percentage)
 * Returns value rounded up to currency precision
 */
export const calculateNewAmountFromRate = (
  displayRate: number,
  markup?: { value?: number; markupAmountType?: MarkupAmountType },
  currency?: SupportedPaymentCurrency
): number => {
  let newAmount: BigNumber;

  if (markup?.markupAmountType === MarkupAmountType.MarkupAmountTypeFixed) {
    // For fixed markup: rate = amount + markup, so amount = rate - markup
    newAmount = new BigNumber(displayRate).minus(markup.value || 0);
  } else {
    // For percentage markup: rate = amount * (1 + markup/100), so amount = rate / (1 + markup/100)
    const markupMultiplier = new BigNumber(1).plus(
      new BigNumber(markup?.value || 0).dividedBy(100)
    );

    newAmount = new BigNumber(displayRate).dividedBy(markupMultiplier);
  }

  // Apply currency precision with round up
  const precision = currency ? getCurrencyPrecision(currency) : 2;

  return newAmount.decimalPlaces(precision).toNumber();
};

/**
 * Calculate the list price (display rate) from base amount and markup
 * This is the inverse of calculateNewAmountFromRate
 * Uses BigNumber to avoid floating-point precision issues
 * Returns value rounded up to currency precision
 */
export const calculateListPriceFromAmount = (
  amount: number,
  markup?: { value?: number; markupAmountType?: MarkupAmountType },
): number => {
  let listPrice: BigNumber;

  if (!markup) {
    listPrice = new BigNumber(amount);
  } else if (markup.markupAmountType === MarkupAmountType.MarkupAmountTypeFixed) {
    // For fixed markup: listPrice = amount + markup
    listPrice = new BigNumber(amount).plus(markup.value || 0);
  } else {
    // For percentage markup: listPrice = amount * (1 + markup/100)
    const markupMultiplier = new BigNumber(1).plus(
      new BigNumber(markup.value || 0).dividedBy(100)
    );

    listPrice = new BigNumber(amount).multipliedBy(markupMultiplier);
  }

  return listPrice.toNumber();
};

/**
 * Get the original list price from originalPrice data
 * Rounds down to 2 decimals to handle floating-point precision issues
 */
export const getOriginalListPrice = (item: ProductItem, currency?: SupportedPaymentCurrency): number => {
  if (!item?.product.originalPrice) return 0;

  const listPrice = calculateListPriceFromAmount(
    item.product.originalPrice.amount,
    item.product.originalPrice.markup || undefined,
  );

  const precision = currency ? getCurrencyPrecision(currency) : 2;

  // Round down to currency precision decimals using BigNumber ROUND_DOWN mode
  // This matches the expected behavior for recalculated prices
  return Number(new BigNumber(listPrice).toFixed(precision, BigNumber.ROUND_DOWN));
};

/**
 * Get the current list price that user sees (for rate overrides, use userInputRate)
 */
export const getCurrentListPrice = (item: ProductItem): number => {
  // For rate overrides, use userInputRate (what user entered)
  if (item?.isRateOverridden && item?.userInputRate !== undefined) {
    return item.userInputRate;
  }

  // Otherwise use calculated listPrice
  return item?.product.sellingPrice?.listPrice || 0;
};
